"use client";

/**
 * useUpload — file upload + job submission + in-flight polling.
 *
 * Orchestrates the full upload→submit→poll→navigate flow.
 * Callers render the UploadZone; this hook owns all side effects.
 */
import { useState, useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import { uploadFile, submitAnalysisJob } from "../api";
import { useJobPoller } from "./useJobPoller";
import type { AnalysisState, UploadProgress } from "../types";
import { initialAnalysisState } from "../types";

interface UseUploadReturn {
  analysisState: AnalysisState;
  handleUpload: (file: File, userId: string) => Promise<void>;
  resetState: () => void;
}

function getProgressMessage(progress: number): string {
  if (progress < 15) return "Uploading file...";
  if (progress < 30) return "File uploaded, starting analysis...";
  if (progress < 45) return "Reading and validating data...";
  if (progress < 60) return "Analyzing spend patterns...";
  if (progress < 75) return "Evaluating vendor performance...";
  if (progress < 88) return "Generating insights and visualizations...";
  if (progress < 95) return "Finalizing analysis...";
  return "Processing results...";
}

export function useUpload(): UseUploadReturn {
  const router = useRouter();
  const [analysisState, setAnalysisState] =
    useState<AnalysisState>(initialAnalysisState);
  const { startPolling, stopPolling } = useJobPoller();

  // Stop polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const resetState = useCallback(() => {
    stopPolling();
    setAnalysisState(initialAnalysisState);
  }, [stopPolling]);

  const handleUpload = useCallback(
    async (
      file: File,
      userId: string
    ) => {
      setAnalysisState({
        isProcessing: true,
        isUploading: true,
        progress: 10,
        progressMessage: "Uploading file...",
        partialResults: null,
        results: null,
        error: null,
      });

      try {
        // Step 1–3: GCS upload
        const uploadResult = await uploadFile(
          file,
          (progress: UploadProgress) => {
            setAnalysisState((prev) => ({
              ...prev,
              progress: progress.percent,
              progressMessage: progress.message,
            }));
          }
        );

        if (uploadResult.duplicate) {
          console.log("Duplicate file, reusing file_id:", uploadResult.file_id);
        }

        setAnalysisState((prev) => ({
          ...prev,
          isUploading: false,
          progress: 15,
          progressMessage: "File uploaded, starting analysis...",
        }));

        // Step 4: Submit job
        const jobData = await submitAnalysisJob(
          uploadResult.file_id,
          userId
        );

        // Cached result — go straight to results page
        if (jobData.status === "CACHED") {
          router.push(`/analyse/jobs/${jobData.job_id}`);
          return;
        }

        setAnalysisState((prev) => ({
          ...prev,
          isUploading: false,
          progress: 20,
          progressMessage: "Analysis job queued, processing...",
        }));

        // Step 5: Poll for completion
        await startPolling(
          jobData.job_id,
          {
            onUpdate: (status) => {
              setAnalysisState((prev) => ({
                ...prev,
                progress: status.progress ?? prev.progress,
                progressMessage:
                  status.current_phase ||
                  getProgressMessage(status.progress ?? prev.progress),
                partialResults: status.result?.is_partial
                  ? status.result
                  : prev.partialResults,
              }));
            },
            onComplete: () => {
              router.push(`/analyse/jobs/${jobData.job_id}`);
            },
            onError: (error) => {
              setAnalysisState({
                ...initialAnalysisState,
                error,
              });
            },
          }
        );
      } catch (err) {
        console.error("Upload/analysis error:", err);
        setAnalysisState({
          ...initialAnalysisState,
          error:
            err instanceof Error
              ? err.message
              : "Failed to process file. Please try again.",
        });
      }
    },
    [router, startPolling]
  );

  return { analysisState, handleUpload, resetState };
}
